/**
 * A visão que responde à pergunta central do sistema: quanto dar de baixa.
 *
 * **Cada linha é um lançamento, não um protocolo.** O grão é
 * (protocolo × arquivo × competência), porque é assim que a baixa acontece: um
 * protocolo recebe R$ 50 mil no demonstrativo de fevereiro e mais R$ 10 mil no
 * de março. Somar os dois diria "baixar R$ 60 mil em março" — e os R$ 50 mil
 * já teriam sido lançados. O acumulado do protocolo aparece como contexto, ao
 * lado, nunca no lugar do valor a lançar.
 *
 * O valor é a soma do pago por guia (bruto): impostos retidos e desconto de
 * auditoria existem apenas no total do arquivo, sem vínculo com guia, e
 * portanto não são rateáveis.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import {
  detalheProtocolo,
  exportarProtocolo,
  visaoProtocolos,
} from '../api/fluxfin';
import {
  Ajuda,
  Alerta,
  Botao,
  Busca,
  LoadingSpinner,
  PageHeader,
  Paginacao,
  SelecaoBuscavel,
  Painel,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import DetalhesDaGuia from '../components/DetalhesDaGuia';
import { useOrdenacao } from '../hooks/useOrdenacao';
import { usePaginacao } from '../hooks/usePaginacao';
import { brl, formatDate, numero } from '../utils/formato';

/* ── Exportar ───────────────────────────────────────────────────────────
 *
 * O arquivo é gerado **no servidor**, não aqui. Três razões: ele sai com todas
 * as guias, não só a página que está na tela; sai sempre igual, tenha sido
 * pedido de que navegador for; e fica registrado na auditoria — é um documento
 * financeiro que vai circular por e-mail.
 */
function BotaoExportar({ protocolo, idArquivo, aoFalhar }) {
  const [gerando, setGerando] = useState('');

  const baixar = async (formato) => {
    setGerando(formato);
    try {
      const r = await exportarProtocolo(protocolo, { formato, idArquivo });
      // O nome vem do servidor, que sabe o padrão; o cabeçalho é a única
      // fonte dele, e sem `filename` o navegador salvaria como "exportar".
      const cd = r.headers['content-disposition'] || '';
      const achado = /filename="?([^";]+)"?/.exec(cd);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = achado ? achado[1] : `baixa-${protocolo}.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Sem o revoke, cada exportação deixa o blob na memória da aba.
      URL.revokeObjectURL(url);
    } catch (e) {
      aoFalhar(mensagemErro(e, 'Não foi possível gerar o arquivo.'));
    } finally {
      setGerando('');
    }
  };

  return (
    <div className="d-flex gap-2">
      <Botao
        variante="secundario"
        pequeno
        icone="bi-file-earmark-spreadsheet"
        onClick={() => baixar('xlsx')}
        disabled={!!gerando}
        title="Planilha com todas as guias deste lançamento, com os valores como número"
      >
        {gerando === 'xlsx' ? 'Gerando…' : 'Excel'}
      </Botao>
      <Botao
        variante="secundario"
        pequeno
        icone="bi-file-earmark-pdf"
        onClick={() => baixar('pdf')}
        disabled={!!gerando}
        title="Versão para imprimir ou anexar"
      >
        {gerando === 'pdf' ? 'Gerando…' : 'PDF'}
      </Botao>
    </div>
  );
}

/* ── A linha do tempo do protocolo ──────────────────────────────────────
 *
 * É o que responde "isto é a primeira parcela ou a terceira?". Sem ela, quem
 * olha a baixa do mês não tem como saber se aquele protocolo já recebeu antes
 * — e é justamente aí que nasce o risco de lançar duas vezes.
 */
function HistoricoProtocolo({ historico, idArquivoAtual }) {
  if (!historico || historico.length <= 1) return null;

  return (
    <div className="p-4" style={{ borderBottom: '1px solid var(--ff-border-soft)' }}>
      <div style={{ fontWeight: 600, fontSize: 13, letterSpacing: 0.2 }}>
        Recebimentos deste protocolo
      </div>
      <p style={{ fontSize: 12, color: 'var(--ff-muted)', margin: '2px 0 10px' }}>
        Cada arquivo foi (ou será) lançado separadamente. O acumulado é só
        referência — não é o valor a baixar.
      </p>
      <Tabela estreita>
        <thead>
          <tr>
            <Th>Competência</Th>
            <Th>Arquivo</Th>
            <Th alinhar="right">Guias</Th>
            <Th alinhar="right">Valor lançado</Th>
            <Th alinhar="right">Acumulado</Th>
          </tr>
        </thead>
        <tbody>
          {historico.map((h) => {
            const atual = h.id_arquivo === idArquivoAtual;
            return (
              <tr
                key={h.id_arquivo}
                style={atual ? { background: 'var(--ff-surface-2)' } : undefined}
              >
                <td className="col-meta">
                  {h.competencia || '—'}
                  {atual && (
                    <span className="ms-2" style={{ fontSize: 11, color: 'var(--ff-acento)' }}>
                      este
                    </span>
                  )}
                </td>
                <td className="col-codigo">{h.arquivo}</td>
                <td className="col-qtd">{numero(h.qtd_guias)}</td>
                <td className="col-valor">{brl(h.valor)}</td>
                <td className="col-qtd" style={{ color: 'var(--ff-muted)' }}>
                  {brl(h.acumulado)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Tabela>
    </div>
  );
}

function DetalheProtocolo({ linha, aoFechar }) {
  const { protocolo, id_arquivo: idArquivo } = linha;
  const [guias, setGuias] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    setCarregando(true);
    // Recorta pelo arquivo: as guias que interessam são as DESTE lançamento.
    detalheProtocolo(protocolo, { id_arquivo: idArquivo })
      .then((r) => {
        setGuias(r.data.guias);
        setHistorico(r.data.historico || []);
      })
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar as guias.')))
      .finally(() => setCarregando(false));
  }, [protocolo, idArquivo]);

  const [guiaDetalhada, setGuiaDetalhada] = useState(null);
  const pag = usePaginacao(guias, 15);
  const total = guias.reduce((s, g) => s + Number(g.valor_pago || 0), 0);

  return (
    <>
      <div
        className="modal-backdrop fade show"
        onClick={aoFechar}
        style={{ background: 'rgba(16,24,40,.5)' }}
      />
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered">
          <div
            className="modal-content"
            style={{ borderRadius: 16, border: '1px solid var(--ff-border)' }}
          >
            <div className="modal-header" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <div>
                <h2 className="ff-painel__titulo">Protocolo {protocolo}</h2>
                <p className="ff-painel__sub">
                  Guias que compõem a baixa de <strong>{linha.arquivo}</strong>
                  {linha.competencia ? ` · competência ${linha.competencia}` : ''}
                </p>
              </div>
              <button className="btn-close" onClick={aoFechar} aria-label="Fechar" />
            </div>

            {/* Exportar sai do cabeçalho e ganha barra própria. No cabeçalho
                ele disputava espaço com o título e empurrava o X para longe;
                aqui fica alinhado à direita, perto da tabela sobre a qual
                age, e some da linha do título. */}
            <div
              className="d-flex justify-content-end px-3 pt-3"
              style={{ borderBottom: '1px solid var(--ff-border-soft)', paddingBottom: 12 }}
            >
              <BotaoExportar
                protocolo={protocolo}
                idArquivo={idArquivo}
                aoFalhar={setErro}
              />
            </div>

            <div className="modal-body p-0">
              <Alerta tipo="erro">{erro}</Alerta>
              {carregando ? (
                <LoadingSpinner />
              ) : (
                <>
                  <HistoricoProtocolo historico={historico} idArquivoAtual={idArquivo} />
                <Tabela estreita>
                  <thead>
                    <tr>
                      <Th>Guia</Th>
                      <Th>Atendimento</Th>
                      <Th alinhar="right">Linhas</Th>
                      <Th alinhar="right">
                        Cobrado
                        <Ajuda
                          titulo="Valor cobrado"
                          origem="Exame_atendimento.vlr_contratante"
                        >
                          O que o Bioslab registra como cobrado nesta guia.
                        </Ajuda>
                      </Th>
                      <Th alinhar="right">
                        Pago
                        <Ajuda
                          titulo="Valor pago"
                          origem="Arquivo da operadora · Valor (R$)"
                        >
                          O que a operadora pagou nesta guia, conforme o demonstrativo.
                        </Ajuda>
                      </Th>
                      <Th alinhar="right">
                        Glosado
                        <Ajuda titulo="Valor glosado" origem="Arquivo da operadora">
                          O que a operadora recusou nesta guia. Vem do extrato de
                          glosa (Unimed) ou das colunas de glosa do próprio
                          demonstrativo (GEAP).
                          <br />
                          <br />
                          Vazio não é zero: significa que nenhum arquivo falou de
                          glosa para esta guia ainda.
                        </Ajuda>
                      </Th>
                      <Th>
                        Fatura
                        <Ajuda titulo="Fatura" origem="Fatura.nr_fatura">
                          Número da fatura no Bioslab que contém esta guia.
                        </Ajuda>
                      </Th>
                      <Th>Competência</Th>
                      <Th alinhar="right">{''}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {pag.paginados.map((g) => (
                      <tr key={g.guia}>
                        <td className="col-codigo">
                          {g.guia}
                          {g.origem === 'manual' && (
                            <div
                              style={{ fontSize: 12, color: '#9a5b06' }}
                              title={`Vinculado à mão por ${g.resolvido_por || '—'}: ${g.justificativa || ''}`}
                            >
                              <i className="bi bi-pencil-square me-1" aria-hidden="true" />
                              vínculo manual
                            </div>
                          )}
                        </td>
                        <td className="col-meta">{formatDate(g.data_atendimento)}</td>
                        <td className="col-qtd">{numero(g.qtd_linhas)}</td>
                        <td className="col-qtd">{brl(g.valor_cobrado)}</td>
                        <td className="col-valor">{brl(g.valor_pago)}</td>
                        <td className="col-qtd">
                          {g.valor_glosado === null || g.valor_glosado === undefined ? (
                            <span
                              style={{ color: 'var(--ff-border-field)' }}
                              title="Nenhum arquivo falou de glosa para esta guia ainda — não é o mesmo que glosa zero."
                            >
                              —
                            </span>
                          ) : Number(g.valor_glosado) > 0 ? (
                            <span style={{ color: 'var(--ff-error-ink)' }}>
                              {brl(g.valor_glosado)}
                            </span>
                          ) : (
                            brl(g.valor_glosado)
                          )}
                        </td>
                        <td className="col-meta">{g.nr_fatura || '—'}</td>
                        <td className="col-meta">{g.competencia || '—'}</td>
                        <td className="col-data" style={{ textAlign: 'right' }}>
                          {/* Desce um nível: os procedimentos desta guia, com a
                              glosa de cada um. A guia diz QUE houve glosa; só o
                              item diz ONDE. */}
                          <Botao
                            variante="secundario"
                            pequeno
                            onClick={() => setGuiaDetalhada(g.guia)}
                            title="Ver os procedimentos desta guia e a glosa de cada um"
                          >
                            Detalhes
                          </Botao>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th colSpan={4}>Total a baixar neste lançamento</th>
                      <th className="col-valor">{brl(total)}</th>
                      <th colSpan={3} />
                    </tr>
                  </tfoot>
                </Tabela>
                </>
              )}
            </div>

            {!carregando && <Paginacao {...pag} rotulo="guias" />}

            <div className="modal-footer" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <Botao variante="secundario" pequeno onClick={aoFechar}>
                Fechar
              </Botao>
            </div>
          </div>
        </div>
      </div>

      {guiaDetalhada && (
        <DetalhesDaGuia
          guia={guiaDetalhada}
          aoFechar={() => setGuiaDetalhada(null)}
        />
      )}
    </>
  );
}

export default function Protocolos() {
  // A visão geral manda para cá com `?arquivo=<id>`: clicar num demonstrativo
  // significa "quero ver o que este arquivo manda baixar", não o cadastro dele.
  const [params, setParams] = useSearchParams();
  const idArquivo = params.get('arquivo') || '';

  const [itens, setItens] = useState([]);
  const [busca, setBusca] = useState('');
  const [competencia, setCompetencia] = useState('');
  // Filtro de arquivo NO CLIENTE, ao lado do de competência. O `?arquivo=`
  // da URL continua existindo e é outra coisa: ele vem de um clique na Visão
  // geral, recorta no servidor e traz o aviso de contexto no topo. Este aqui
  // é para recortar o que já está na tela, sem recarregar.
  const [arquivo, setArquivo] = useState('');
  const [operadora, setOperadora] = useState('');
  const [selecionado, setSelecionado] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  // A competência é filtrada **no cliente**, e por isso é um seletor.
  //
  // Antes era um campo de texto que refazia a consulta a cada tecla: digitar o
  // primeiro dígito mandava `competencia=0` ao servidor, que não casa com
  // nada, e a tela esvaziava. Pior, os próprios filtros sumiam junto — eles só
  // eram renderizados quando havia itens — e não havia como desfazer.
  //
  // Esta consulta não tem limite de linhas, então todos os lançamentos já
  // estão aqui: filtrar no cliente é instantâneo e, sendo um seletor montado a
  // partir do próprio dado, não existe valor que não exista.
  useEffect(() => {
    setCarregando(true);
    visaoProtocolos(idArquivo ? { id_arquivo: idArquivo } : undefined)
      .then((r) => setItens(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar os protocolos.')))
      .finally(() => setCarregando(false));
  }, [idArquivo]);

  // Os filtros são EM CASCATA e **cada um ignora a si mesmo** ao montar as
  // próprias opções — o mesmo padrão da Auditoria de glosa, e pelo mesmo
  // motivo: oferecer um valor que não produz resultado nenhum não é neutro,
  // faz duvidar do sistema; mas se a lista encolhesse até sobrar só o
  // escolhido, não haveria como trocar sem limpar antes.
  const recorte = useCallback(
    (ignorando) =>
      itens.filter((i) => {
        if (ignorando !== 'competencia' && competencia && i.competencia !== competencia)
          return false;
        if (ignorando !== 'arquivo' && arquivo && i.arquivo !== arquivo) return false;
        if (ignorando !== 'operadora' && operadora && i.operadora !== operadora)
          return false;
        return true;
      }),
    [itens, competencia, arquivo, operadora]
  );

  const competencias = useMemo(() => {
    const vistas = [
      ...new Set(recorte('competencia').map((i) => i.competencia).filter(Boolean)),
    ];
    // MM/AAAA não ordena como texto: compara ano e depois mês.
    return vistas.sort((a, b) => {
      const [ma, aa] = a.split('/');
      const [mb, ab] = b.split('/');
      return `${ab}${mb}`.localeCompare(`${aa}${ma}`);
    });
  }, [recorte]);

  const operadoras = useMemo(
    () => [...new Set(recorte('operadora').map((i) => i.operadora).filter(Boolean))].sort(),
    [recorte]
  );

  const arquivos = useMemo(
    () => [...new Set(recorte('arquivo').map((i) => i.arquivo).filter(Boolean))].sort(),
    [recorte]
  );

  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return itens.filter((i) => {
      if (competencia && i.competencia !== competencia) return false;
      if (arquivo && i.arquivo !== arquivo) return false;
      if (operadora && i.operadora !== operadora) return false;
      if (!t) return true;
      return (
        i.protocolo?.toLowerCase().includes(t) ||
        i.arquivo?.toLowerCase().includes(t) ||
        i.operadora?.toLowerCase().includes(t)
      );
    });
  }, [itens, busca, competencia, arquivo, operadora]);

  // O nome do arquivo vem nas próprias linhas — não precisa de outra consulta.
  const nomeArquivo = idArquivo && itens.length > 0 ? itens[0].arquivo : null;

  const ord = useOrdenacao(filtrados, 'valor_baixa', false);
  const pag = usePaginacao(ord.ordenados, 15);

  // O total acompanha o filtro — senão o rodapé contradiz a tabela.
  const totalFiltrado = filtrados.reduce((s, i) => s + Number(i.valor_baixa || 0), 0);

  return (
    <>
      <PageHeader
        titulo="Baixa por protocolo"
        subtitulo="Cada linha é um lançamento: um protocolo, num arquivo, numa competência. É este o valor a baixar no Nasajon."
        acoes={
          idArquivo && (
            <Botao
              variante="secundario"
              icone="bi-x-lg"
              onClick={() => setParams({}, { replace: true })}
            >
              Ver todos os arquivos
            </Botao>
          )
        }
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      {idArquivo && (
        <div className="ff-aviso ff-aviso--info">
          <i className="bi bi-funnel" aria-hidden="true" />
          <div>
            Mostrando apenas o que <strong>{nomeArquivo || 'este arquivo'}</strong> manda
            baixar. Outros recebimentos dos mesmos protocolos, vindos de arquivos
            anteriores, ficam de fora — eles já foram lançados na competência deles.
          </div>
        </div>
      )}

      {itens.length > 0 && (
        <div className="mb-3">
          <Painel
            titulo="Filtros"
            acoes={
              (busca || competencia || arquivo || operadora) && (
                <Botao
                  variante="secundario"
                  pequeno
                  onClick={() => {
                    setBusca('');
                    setCompetencia('');
                    setArquivo('');
                    setOperadora('');
                  }}
                >
                  Limpar filtros
                </Botao>
              )
            }
          >
            <div className="p-3 d-flex flex-wrap gap-3 align-items-end">
              <SelecaoBuscavel
                rotulo="Competência"
                largura={150}
                textoTodas="Todas"
                valor={competencia}
                opcoes={competencias.map((c) => ({ valor: c, texto: c }))}
                aoMudar={setCompetencia}
              />
              <SelecaoBuscavel
                rotulo="Arquivo"
                largura={250}
                textoTodas="Todos"
                valor={arquivo}
                opcoes={arquivos.map((a) => ({ valor: a, texto: a }))}
                aoMudar={setArquivo}
              />
              <SelecaoBuscavel
                rotulo="Operadora"
                largura={170}
                textoTodas="Todas"
                valor={operadora}
                opcoes={operadoras.map((o) => ({ valor: o, texto: o }))}
                aoMudar={setOperadora}
              />
              <div style={{ minWidth: 240, flex: '1 1 240px' }}>
                {/* Mesmo rótulo e mesma escala dos seletores ao lado — três
                    estilos de filtro na mesma barra era o que estava errado. */}
                <label style={{ fontSize: 12, color: 'var(--ff-muted)' }}>Buscar</label>
                <Busca
                  valor={busca}
                  onChange={setBusca}
                  placeholder="Protocolo, arquivo ou operadora"
                  id="busca-protocolos"
                />
              </div>
            </div>
          </Painel>
        </div>
      )}

      <Painel
        titulo="Baixas a lançar"
        subtitulo={
          filtrados.length > 0
            ? `${numero(filtrados.length)} lançamento${filtrados.length === 1 ? '' : 's'} · ${brl(totalFiltrado)} a baixar`
            : 'Nenhum protocolo conciliado ainda'
        }
        rodape={filtrados.length > 0 && <Paginacao {...pag} rotulo="protocolos" />}
      >
        {carregando ? (
          <LoadingSpinner />
        ) : itens.length === 0 ? (
          <Vazio
            icone="bi-cash-stack"
            titulo="Nenhum protocolo conciliado"
            descricao="Protocolos aparecem aqui depois que um arquivo confirmado tem suas guias resolvidas no Bioslab. Enquanto a conexão não estiver liberada, as guias ficam listadas em Pendências."
          />
        ) : filtrados.length === 0 ? (
          <Vazio
            icone="bi-search"
            titulo="Nenhum lançamento corresponde ao filtro"
            descricao="Ajuste a busca, a competência ou o arquivo."
            acao={
              <Botao
                variante="secundario"
                onClick={() => {
                  setBusca('');
                  setCompetencia('');
                  setArquivo('');
                }}
              >
                Limpar filtros
              </Botao>
            }
          />
        ) : (
          <Tabela>
            <thead>
              <tr>
                <Th campo="protocolo" estado={ord}>
                  Protocolo
                  <Ajuda titulo="Protocolo" origem="Fat_Apresentacao.NR_protocolo">
                    O agrupamento de faturas enviado à operadora para cobrança — também
                    chamado de processo. É a unidade em que a baixa acontece no Nasajon.
                    Vem do Bioslab, resolvido a partir da guia.
                  </Ajuda>
                </Th>
                <Th campo="arquivo" estado={ord}>
                  Arquivo
                  <Ajuda titulo="Arquivo" origem="Demonstrativo enviado no lançamento">
                    O demonstrativo de onde veio este dinheiro. A baixa é feita por
                    arquivo: o que veio num demonstrativo anterior já foi lançado na
                    competência dele.
                  </Ajuda>
                </Th>
                <Th campo="competencia" estado={ord}>
                  Competência
                  <Ajuda titulo="Competência" origem="Cabeçalho do demonstrativo">
                    Mês em que a operadora efetuou o pagamento — não é o mês do
                    atendimento. Um mesmo protocolo pode aparecer em várias
                    competências, uma por recebimento.
                  </Ajuda>
                </Th>
                <Th campo="qtd_guias" estado={ord} alinhar="right">
                  Guias
                </Th>
                <Th campo="valor_cobrado" estado={ord} alinhar="right">
                  Cobrado (Bioslab)
                  <Ajuda titulo="Valor cobrado" origem="Exame_atendimento.vlr_contratante">
                    Quanto o Bioslab registra que foi cobrado nas guias deste protocolo —
                    a soma dos exames de cada atendimento. Lido do Bioslab, somente
                    leitura.
                  </Ajuda>
                </Th>
                <Th campo="valor_baixa" estado={ord} alinhar="right">
                  Valor a baixar
                  <Ajuda
                    titulo="Valor a baixar"
                    origem="Arquivo da operadora · coluna Valor (R$)"
                  >
                    Quanto a operadora pagou <strong>neste arquivo</strong>, para este
                    protocolo. É o número que vai para o Nasajon. Não inclui o que o
                    mesmo protocolo recebeu em demonstrativos anteriores — aquilo já foi
                    lançado. Usa o valor BRUTO: impostos retidos e descontos gerais
                    existem só no total do arquivo e não são rateáveis por protocolo.
                  </Ajuda>
                </Th>
                <Th campo="valor_glosado" estado={ord} alinhar="right">
                  Glosado
                  <Ajuda titulo="Valor glosado" origem="Arquivo da operadora">
                    O que a operadora <strong>recusou</strong> nas guias deste
                    lançamento. Vem do extrato de glosa (Unimed) ou das colunas de
                    glosa do próprio demonstrativo (GEAP).
                    <br />
                    <br />
                    Célula vazia não é zero: significa que{' '}
                    <strong>nenhum arquivo falou de glosa</strong> para estas guias
                    ainda — na Unimed, porque o extrato vem num segundo documento que
                    pode não ter chegado.
                  </Ajuda>
                </Th>
                <Th campo="diferenca" estado={ord} alinhar="right">
                  Diferença
                  <Ajuda titulo="Diferença" origem="cobrado − pago − glosado">
                    O que sobrou <strong>sem explicação</strong> neste lançamento.
                    Positivo é dinheiro que faltou; negativo é pagamento acima do
                    cobrado.
                    <br />
                    <br />
                    Não é saldo a receber: aqui só entram guias que já apareceram num
                    demonstrativo. E não é glosa — a glosa já foi descontada, é
                    justamente o que sobra depois dela.
                    <br />
                    <br />
                    Vazio significa que <strong>não dá para calcular</strong>: falta o
                    cobrado do Bioslab, ou a glosa desta competência ainda não chegou.
                  </Ajuda>
                </Th>
                <Th>Atendimentos</Th>
                <Th alinhar="right">{''}</Th>
              </tr>
            </thead>
            <tbody>
              {pag.paginados.map((i) => (
                <tr
                  key={`${i.protocolo}|${i.id_arquivo}`}
                  className="ff-linha-clicavel"
                  onClick={() => setSelecionado(i)}
                >
                  <td className="col-codigo">
                    {i.protocolo}
                    {i.qtd_arquivos_protocolo > 1 && (
                      <div
                        style={{ fontSize: 12, color: 'var(--ff-muted)' }}
                        title={`Este protocolo recebeu em ${i.qtd_arquivos_protocolo} arquivos, somando ${brl(i.acumulado_protocolo)}. Cada um é lançado na sua competência.`}
                      >
                        <i className="bi bi-clock-history me-1" aria-hidden="true" />
                        {i.qtd_arquivos_protocolo}º recebimento · acum. {brl(i.acumulado_protocolo)}
                      </div>
                    )}
                  </td>
                  <td className="col-codigo">
                    {i.arquivo}
                    <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>{i.operadora}</div>
                  </td>
                  <td className="col-meta">{i.competencia || '—'}</td>
                  <td className="col-qtd">
                    {numero(i.qtd_guias)}
                    {i.qtd_manuais > 0 && (
                      <div
                        style={{ fontSize: 12, color: '#9a5b06' }}
                        title="Guias vinculadas ao protocolo manualmente, não pela consulta ao Bioslab. Entram na mesma soma."
                      >
                        <i className="bi bi-pencil-square me-1" aria-hidden="true" />
                        {i.qtd_manuais} manual{i.qtd_manuais === 1 ? '' : 'is'}
                      </div>
                    )}
                  </td>
                  <td className="col-qtd">{brl(i.valor_cobrado)}</td>
                  <td className="col-valor">{brl(i.valor_baixa)}</td>
                  <td className="col-qtd">
                    {i.valor_glosado === null || i.valor_glosado === undefined ? (
                      <span
                        style={{ color: 'var(--ff-border-field)' }}
                        title="Nenhum arquivo falou de glosa para estas guias ainda — não é o mesmo que glosa zero."
                      >
                        —
                      </span>
                    ) : Number(i.valor_glosado) > 0 ? (
                      <span style={{ color: 'var(--ff-error-ink)' }}>
                        {brl(i.valor_glosado)}
                      </span>
                    ) : (
                      brl(i.valor_glosado)
                    )}
                  </td>
                  <td
                    className="col-qtd"
                    style={
                      // Positivo e dinheiro que faltou — e o que pede acao.
                      Number(i.diferenca) > 0 ? { color: '#9a5b06' } : undefined
                    }
                  >
                    {i.diferenca === null || i.diferenca === undefined ? (
                      <span
                        style={{ color: 'var(--ff-border-field)' }}
                        title="Falta o cobrado do Bioslab ou a glosa desta competência ainda não chegou."
                      >
                        —
                      </span>
                    ) : (
                      brl(i.diferenca)
                    )}
                  </td>
                  <td className="col-meta">
                    {formatDate(i.atendimento_de)} – {formatDate(i.atendimento_ate)}
                  </td>
                  <td className="col-data">
                    <i className="bi bi-chevron-right" aria-hidden="true" />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={5}>
                  Total{busca || competencia || idArquivo ? ' (filtrado)' : ''} —{' '}
                  {filtrados.length} lançamento{filtrados.length === 1 ? '' : 's'}
                </th>
                <th className="col-valor">{brl(totalFiltrado)}</th>
                <th colSpan={3} />
              </tr>
            </tfoot>
          </Tabela>
        )}
      </Painel>

      {selecionado && (
        <DetalheProtocolo linha={selecionado} aoFechar={() => setSelecionado(null)} />
      )}
    </>
  );
}
